import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/userStore';
import type {
  Exercise,
  WorkoutPlan,
  WorkoutSession,
  ExerciseLog,
  StrengthRecord,
  WorkoutAchievement,
  MuscleGroup,
  SetData,
} from '@/types/workout';

// ─── Упражнения ──────────────────────────────────────────────────────────────

export function useExercises(muscleGroup?: MuscleGroup) {
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let query = supabase.from('exercises').select('*').order('name');
    if (muscleGroup) query = query.eq('muscle_group', muscleGroup);

    query.then(({ data, error }) => {
      if (!error && data) setExercises(data);
      setLoading(false);
    });
  }, [muscleGroup]);

  return { exercises, loading };
}

export function useExercise(id: string | null) {
  const [exercise, setExercise] = useState<Exercise | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    supabase.from('exercises').select('*').eq('id', id).single().then(({ data }) => {
      setExercise(data);
      setLoading(false);
    });
  }, [id]);

  return { exercise, loading };
}

export function useExercisesByIds(ids: string[]) {
  const [exercises, setExercises] = useState<Record<string, Exercise>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ids.length) { setLoading(false); return; }
    supabase.from('exercises').select('*').in('id', ids).then(({ data }) => {
      const map: Record<string, Exercise> = {};
      data?.forEach((ex) => { map[ex.id] = ex; });
      setExercises(map);
      setLoading(false);
    });
  }, [JSON.stringify(ids)]);

  return { exercises, loading };
}

// ─── Тренировочные планы ──────────────────────────────────────────────────────

export function useWorkoutPlans() {
  const { profile } = useUserStore();
  const [plans, setPlans] = useState<WorkoutPlan[]>([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!profile?.id) { setLoading(false); return; }
    const { data } = await supabase
      .from('workout_plans')
      .select('*')
      .eq('user_id', profile.id)
      .eq('is_active', true)
      .order('created_at', { ascending: false });
    setPlans(data ?? []);
    setLoading(false);
  }, [profile?.id]);

  useEffect(() => { fetch(); }, [fetch]);

  const deletePlan = async (id: string) => {
    await supabase.from('workout_plans').update({ is_active: false }).eq('id', id);
    setPlans((prev) => prev.filter((p) => p.id !== id));
  };

  return { plans, loading, refetch: fetch, deletePlan };
}

export function useWorkoutPlan(id: string | null) {
  const [plan, setPlan] = useState<WorkoutPlan | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    supabase.from('workout_plans').select('*').eq('id', id).single().then(({ data }) => {
      setPlan(data);
      setLoading(false);
    });
  }, [id]);

  async function updatePlanData(newPlanData: WorkoutPlan['plan_data']) {
    if (!id || !plan) return;
    const updated = { ...plan, plan_data: newPlanData };
    setPlan(updated as WorkoutPlan);
    await supabase.from('workout_plans').update({ plan_data: newPlanData }).eq('id', id);
  }

  return { plan, loading, updatePlanData };
}

// ─── История тренировок ───────────────────────────────────────────────────────

export function useWorkoutSessions(limit = 20) {
  const { profile } = useUserStore();
  const [sessions, setSessions] = useState<WorkoutSession[]>([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!profile?.id) return;
    const { data } = await supabase
      .from('workout_sessions')
      .select('*')
      .eq('user_id', profile.id)
      .not('finished_at', 'is', null)
      .order('started_at', { ascending: false })
      .limit(limit);
    setSessions(data ?? []);
    setLoading(false);
  }, [profile?.id, limit]);

  useEffect(() => { fetch(); }, [fetch]);

  async function deleteSession(id: string) {
    await supabase.from('workout_sessions').delete().eq('id', id);
    setSessions((prev) => prev.filter((s) => s.id !== id));
  }

  return { sessions, loading, refetch: fetch, deleteSession };
}

// ─── Прогресс и рекорды ───────────────────────────────────────────────────────

export function useStrengthRecords(exerciseIds?: string[]) {
  const { profile } = useUserStore();
  const [records, setRecords] = useState<StrengthRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile?.id) return;
    let query = supabase
      .from('strength_records')
      .select('*')
      .eq('user_id', profile.id);
    if (exerciseIds?.length) query = query.in('exercise_id', exerciseIds);

    query.then(({ data }) => {
      setRecords(data ?? []);
      setLoading(false);
    });
  }, [profile?.id, JSON.stringify(exerciseIds)]);

  return { records, loading };
}

// Статистика для экрана прогресса
export function useWorkoutStats() {
  const { profile } = useUserStore();
  const [stats, setStats] = useState({
    totalSessions: 0,
    totalTonnage: 0,
    currentStreak: 0,
    thisWeekSessions: 0,
    avgRating: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile?.id) return;

    (async () => {
      const { data } = await supabase
        .from('workout_sessions')
        .select('started_at, total_tonnage_kg, rating, finished_at')
        .eq('user_id', profile.id)
        .not('finished_at', 'is', null)
        .order('started_at', { ascending: false });

      if (!data) { setLoading(false); return; }

      const totalSessions = data.length;
      const totalTonnage = data.reduce((s, r) => s + (r.total_tonnage_kg ?? 0), 0);
      const ratings = data.filter((r) => r.rating).map((r) => r.rating!);
      const avgRating = ratings.length ? ratings.reduce((a, b) => a + b, 0) / ratings.length : 0;

      // Сессии за текущую неделю
      const weekStart = new Date();
      weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
      weekStart.setHours(0, 0, 0, 0);
      const thisWeekSessions = data.filter((r) => new Date(r.started_at) >= weekStart).length;

      // Серия (consecutive weeks with at least 1 session)
      let currentStreak = 0;
      const sessionDates = data.map((r) => new Date(r.started_at));
      const now = new Date();
      for (let w = 0; w < 52; w++) {
        const weekEnd = new Date(now);
        weekEnd.setDate(weekEnd.getDate() - w * 7);
        const weekStartW = new Date(weekEnd);
        weekStartW.setDate(weekStartW.getDate() - 7);
        const hasSession = sessionDates.some((d) => d >= weekStartW && d < weekEnd);
        if (hasSession) currentStreak++;
        else if (w > 0) break;
      }

      setStats({ totalSessions, totalTonnage, currentStreak, thisWeekSessions, avgRating });
      setLoading(false);
    })();
  }, [profile?.id]);

  return { stats, loading };
}

// ─── Запись тренировки ────────────────────────────────────────────────────────

export async function saveWorkoutSession(params: {
  user_id: string;
  plan_id: string | null;
  session_index: number | null;
  session_name: string;
  started_at: Date;
  finished_at: Date;
  exercise_logs: {
    exercise_id: string;
    planned_sets: number | null;
    planned_reps: string | null;
    sets_data: SetData[];
    position: number;
  }[];
  rating: 1 | 2 | 3 | 4 | 5 | null;
  notes: string | null;
  plan_week: number;
}) {
  const duration = Math.round((params.finished_at.getTime() - params.started_at.getTime()) / 1000);

  // Считаем итоги
  let total_tonnage = 0;
  let total_sets = 0;

  const logsWithTotals = params.exercise_logs.map((log) => {
    const actual_sets = log.sets_data.length;
    const total_reps = log.sets_data.reduce((s, set) => s + set.reps, 0);
    const tonnage = log.sets_data.reduce(
      (s, set) => s + set.reps * (set.weight_kg ?? 0),
      0
    );

    // е1ПМ по лучшему подходу (Эпли)
    let estimated_1rm: number | null = null;
    const weightedSets = log.sets_data.filter((s) => s.weight_kg && s.weight_kg > 0);
    if (weightedSets.length > 0) {
      estimated_1rm = Math.max(
        ...weightedSets.map((s) => s.weight_kg! * (1 + s.reps / 30))
      );
      estimated_1rm = Math.round(estimated_1rm * 10) / 10;
    }

    total_tonnage += tonnage;
    total_sets += actual_sets;

    return {
      ...log,
      actual_sets,
      total_reps,
      total_tonnage_kg: tonnage,
      estimated_1rm,
    };
  });

  // Создаём сессию
  const { data: session, error: sessionError } = await supabase
    .from('workout_sessions')
    .insert({
      user_id: params.user_id,
      plan_id: params.plan_id,
      session_index: params.session_index,
      session_name: params.session_name,
      started_at: params.started_at.toISOString(),
      finished_at: params.finished_at.toISOString(),
      duration_seconds: duration,
      total_tonnage_kg: total_tonnage,
      total_sets,
      exercises_completed: params.exercise_logs.length,
      rating: params.rating,
      notes: params.notes,
      plan_week: params.plan_week,
    })
    .select()
    .single();

  if (sessionError || !session) {
    throw new Error(sessionError?.message ?? 'Ошибка сохранения тренировки');
  }

  // Сохраняем логи упражнений
  const logRows = logsWithTotals.map((log) => ({
    session_id: session.id,
    user_id: params.user_id,
    exercise_id: log.exercise_id,
    planned_sets: log.planned_sets,
    planned_reps: log.planned_reps,
    sets_data: log.sets_data,
    actual_sets: log.actual_sets,
    total_reps: log.total_reps,
    total_tonnage_kg: log.total_tonnage_kg,
    estimated_1rm: log.estimated_1rm,
    position: log.position,
  }));

  await supabase.from('exercise_logs').insert(logRows);

  // Авто-инкремент недели: если завершены все тренировки текущей недели
  if (params.plan_id && params.plan_week) {
    const { data: planRow } = await supabase
      .from('workout_plans')
      .select('days_per_week, current_week')
      .eq('id', params.plan_id)
      .single();

    if (planRow && planRow.current_week === params.plan_week) {
      const { count } = await supabase
        .from('workout_sessions')
        .select('id', { count: 'exact', head: true })
        .eq('plan_id', params.plan_id)
        .eq('plan_week', params.plan_week)
        .not('finished_at', 'is', null);

      if ((count ?? 0) >= planRow.days_per_week) {
        await supabase
          .from('workout_plans')
          .update({ current_week: params.plan_week + 1 })
          .eq('id', params.plan_id);
      }
    }
  }

  // Обновляем рекорды силы для упражнений с весом
  for (const log of logsWithTotals) {
    const bestSet = log.sets_data
      .filter((s) => s.weight_kg && s.weight_kg > 0)
      .sort((a, b) => (b.weight_kg! * (1 + b.reps / 30)) - (a.weight_kg! * (1 + a.reps / 30)))[0];

    if (bestSet) {
      await supabase.rpc('update_strength_record', {
        p_user_id: params.user_id,
        p_exercise_id: log.exercise_id,
        p_weight_kg: bestSet.weight_kg,
        p_reps: bestSet.reps,
        p_session_date: params.started_at.toISOString(),
      });
    }
  }

  return { session, total_tonnage };
}

// ─── Ачивки ───────────────────────────────────────────────────────────────────

export function useWorkoutAchievements() {
  const { profile } = useUserStore();
  const [achievements, setAchievements] = useState<WorkoutAchievement[]>([]);

  useEffect(() => {
    if (!profile?.id) return;
    supabase
      .from('workout_achievements')
      .select('*')
      .eq('user_id', profile.id)
      .order('unlocked_at', { ascending: false })
      .then(({ data }) => setAchievements(data ?? []));
  }, [profile?.id]);

  return { achievements };
}

export async function checkAndAwardAchievements(
  userId: string,
  sessionData: { total_tonnage: number; sessions_count: number; streak_weeks: number }
) {
  const toCheck: Array<{
    key: string;
    name: string;
    emoji: string;
    condition: boolean;
    share_data?: object;
  }> = [
    {
      key: 'first_workout',
      name: 'Первый шаг',
      emoji: '🚀',
      condition: sessionData.sessions_count === 1,
    },
    {
      key: 'streak_3',
      name: '3 недели подряд',
      emoji: '🎯',
      condition: sessionData.streak_weeks >= 3,
      share_data: { streak: sessionData.streak_weeks },
    },
    {
      key: 'streak_5',
      name: '5 недель подряд',
      emoji: '🔥',
      condition: sessionData.streak_weeks >= 5,
      share_data: { streak: sessionData.streak_weeks },
    },
    {
      key: 'tonnage_1000',
      name: 'Первая тонна',
      emoji: '⚡',
      condition: sessionData.total_tonnage >= 1000,
      share_data: { tonnage: Math.round(sessionData.total_tonnage) },
    },
    {
      key: 'tonnage_10000',
      name: '10 тонн поднято',
      emoji: '💎',
      condition: sessionData.total_tonnage >= 10000,
      share_data: { tonnage: Math.round(sessionData.total_tonnage) },
    },
  ];

  for (const a of toCheck) {
    if (!a.condition) continue;
    await supabase.from('workout_achievements').upsert(
      {
        user_id: userId,
        achievement_key: a.key,
        achievement_name: a.name,
        achievement_emoji: a.emoji,
        share_data: a.share_data ?? null,
      },
      { onConflict: 'user_id,achievement_key', ignoreDuplicates: true }
    );
  }
}
